/**
 * siteLoadOnce(url, opts?)
 * Loads a script or stylesheet exactly once per page.
 * Returns a Promise that resolves when the resource is ready.
 *
 * Usage:
 *   await window.siteLoadOnce('https://cdn.../lib.css');
 *   await window.siteLoadOnce('https://cdn.../lib.js', { async: true });
 */
window.siteLoadOnce = window.siteLoadOnce || (function () {
  const cache = new Map();

  function isCSS(url) {
    return /\.css(\?|#|$)/i.test(url);
  }

  function alreadyInDOM(url, kind) {
    if (kind === 'css') return !!document.querySelector(`link[rel="stylesheet"][href="${url}"]`);
    return !!document.querySelector(`script[src="${url}"]`);
  }

  return function siteLoadOnce(url, opts = {}) {
    if (!url) return Promise.resolve(false);
    if (cache.has(url)) return cache.get(url);

    const kind = isCSS(url) ? 'css' : 'js';

    if (alreadyInDOM(url, kind)) {
      const p = Promise.resolve(true);
      cache.set(url, p);
      return p;
    }

    const p = new Promise((resolve, reject) => {
      if (kind === 'css') {
        const link = document.createElement('link');
        link.rel = opts.rel || 'stylesheet';
        link.href = url;
        link.onload  = () => resolve(true);
        link.onerror = () => reject(new Error(`siteLoadOnce: failed to load CSS ${url}`));
        document.head.appendChild(link);
        return;
      }

      const script = document.createElement('script');
      script.src = url;
      if (opts.module) {
        // ES modules are always deferred — type=module is mandatory for import statements
        script.type = 'module';
      } else {
        // async: true → no implicit defer (async already ensures non-blocking)
        script.defer = opts.defer !== undefined ? !!opts.defer : !opts.async;
      }
      if (opts.async) script.async = true;
      if (opts.attrs) opts.attrs.forEach(attr => script.setAttribute(attr, ''));
      script.onload  = () => resolve(true);
      script.onerror = () => reject(new Error(`siteLoadOnce: failed to load JS ${url}`));
      document.head.appendChild(script);
    });

    cache.set(url, p);
    return p;
  };
})();

// ─────────────────────────────────────────────────────────────────────────────
// External scripts lazy loader
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load one external script lazily.
 * Waits until an element carrying `detectionAttr` approaches the viewport,
 * then calls siteLoadOnce to inject the script.
 *
 * @param {string} name          Display name for logs
 * @param {string} url           CDN URL of the script
 * @param {string} detectionAttr Attribute that signals this lib is needed
 * @param {string} scriptAttr    'async' | 'defer'
 */
export function externalScriptLoader(name, url, detectionAttr, scriptAttr) {
  const selector = detectionAttr.startsWith('[') ? detectionAttr : `[${detectionAttr}]`;
  const el = document.querySelector(selector);
  if (!el) return;

  console.log(`[Bolk] ${name} — will load when visible (${selector})`);

  const parts    = (scriptAttr || '').trim().split(/\s+/).filter(Boolean);
  const mode     = parts.find(p => p === 'async' || p === 'defer');
  const isModule = parts.includes('module');
  const attrs    = parts.filter(p => p !== 'async' && p !== 'defer' && p !== 'module');
  const opts     = {
    ...(mode     ? { [mode]: true }  : {}),
    ...(isModule ? { module: true }  : {}),
    ...(attrs.length ? { attrs }     : {}),
  };
  const rawThreshold = (window.BolkConfig && window.BolkConfig.threshold) || '50vh';
  const threshold = String(rawThreshold)
    .replace(/(-?\d*\.?\d+)vh/g, (_, n) => `${(parseFloat(n) / 100) * window.innerHeight}px`)
    .replace(/(-?\d*\.?\d+)vw/g, (_, n) => `${(parseFloat(n) / 100) * window.innerWidth}px`);

  if (!('IntersectionObserver' in window)) {
    window.siteLoadOnce(url, opts);
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    if (entries[0].isIntersecting) {
      window.siteLoadOnce(url, opts);
      obs.disconnect();
    }
  }, { rootMargin: threshold });

  observer.observe(el);
}

/**
 * Run externalScriptLoader for every entry in the provided scripts object.
 * Called automatically by Bolk with BolkExternalScripts on DOMContentLoaded.
 *
 * @param {Record<string, [string, string, string]>} scripts
 */
export function loadAllExternalScripts(scripts) {
  for (const [name, [url, detectionAttr, scriptAttr]] of Object.entries(scripts)) {
    externalScriptLoader(name, url, detectionAttr, scriptAttr);
  }
}
